/**
 * @module json-schema
 */
/** @pratico/json-schema */
/******************************************************************************
 * The MIT License (MIT)                                                      *
 *                                                                            *
 * Copyright (c) 2016 Simon "Tenry" Burchert                                  *
 *                                                                            *
 * Permission is hereby granted, free of charge, to any person obtaining a    *
 * copy of this software and associated documentation files (the "Software"), *
 * to deal in the Software without restriction, including without limitation  *
 * the rights to use, copy, modify, merge, publish, distribute, sublicense,   *
 * and/or sell copies of the Software, and to permit persons to whom the      *
 * Software is furnished to do so, subject to the following conditions:       *
 *                                                                            *
 * The above copyright notice and this permission notice shall be included in *
 * all copies or substantial portions of the Software.                        *
 *                                                                            *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR *
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,   *
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL    *
 * THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER *
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING    *
 * FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER        *
 * EALINGS IN THE SOFTWARE.                                                   *
 ******************************************************************************/

export class EventEmitter {
  private eventListeners: Map<Function, Function[]>;

  constructor() {
    this.eventListeners = new Map();
  }

  on(event: Function, listener: Function) {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, [listener]);
    } else {
      const eventObj = this.eventListeners.get(event);
      if (eventObj !== undefined) {
        eventObj.push(listener);
      }
    }

    return new Listener(this, event, listener);
  }

  addListener(event: Function, listener: Function) {
    return this.on(event, listener);
  }

  removeListener(id?: Listener): void;
  removeListener(event: Function, listener?: Function): void;

  removeListener() {
    if (arguments.length === 0) {
      this.eventListeners.clear();
    } else if (arguments.length === 1 && typeof arguments[0] === 'object') {
      let id = arguments[0];
      this.removeListener(id.event, id.listener);
    } else if (arguments.length >= 1) {
      let event = arguments[0] as Function;
      let listener = arguments[1] as Function;

      if (this.eventListeners.has(event)) {
        const listeners = this.eventListeners.get(event) as Function[];
        const idx = listeners.indexOf(listener);
        if (listeners !== undefined) {
          while (!listener || idx  !== -1) {
            if (idx !== undefined) {
              listeners.splice(idx, 1);
            }
          }
        }
      }
    }
  }

  /**
   * @typeparam T The event handler signature.
   */
  registerEvent<T extends Function>() {
    let eventBinder = (handler: T) => {
      return this.addListener(eventBinder, handler);
    };

    return eventBinder;
  }

  /**
   * Emit event. Calls all bound listeners with args.
   */
  protected emit(event: Function, ...args: any[]) {
    if (this.eventListeners.has(event)) {
      const events = this.eventListeners.get(event);
      for (const listener of events as Function[]) {
        listener(...args);
      }
    }
  }
}

export class Listener {
  constructor(public owner: EventEmitter, public event: Function, public listener: Function) {}

  unbind() {
    this.owner.removeListener(this);
  }
}
